import { useEffect, useState } from 'react';
import withComponentAuth from '../../hoc/withComponentAuth';
import { Box, Typography } from '@mui/material';
import { SmartFilter } from '../../components/common/SmartFilter';
import { headerToolTable } from '../../utils/constants';
import Pagination from '../../components/atoms/Pagination';
import { IManageTool } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import useSnackbar from '../../hooks/useSnackbar';
import AddToolModal from './AddToolModal';
import { ToolList } from '../../components/ui/list/ManageTool';
import { addTool, editTool, GetManageTool } from '../../services/managetool';
import axios from 'axios';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

interface FormData {
  id?: string;
  name: string;
  description: string;
  image: string;
  code?: string;
}

const ToolTable = () => {
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [toolList, setToolList] = useState<IManageTool[]>([]);
  const [totalMaterial, setTotalMaterial] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [addToolModal, setAddToolModal] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    id: '',
    name: '',
    description: '',
    image: '',
    code: '',
  });
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    fetchTools(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, searchQuery]);

  // Fetch users from API
  const fetchTools = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
  ) => {
    try {
      setIsLoading(true);
      const response = await GetManageTool({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
      });
      setToolList(response.data);
      setTotalMaterial(response.pagination.totalRecords);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const refeshList = () => {
    fetchTools(page, rowsPerPage, searchQuery);
  };

  const handleSaveRefresh = () => {
    refeshList();
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleFilterRefresh = () => {
    console.log('FILTER HAPPENED');
  };

  //   -----------------Calling API for add material-------------------
  async function handleSubmit() {
    console.log(
      'generatePresignedUrl(formData.image)',
      await generatePresignedUrl(formData.image),
    );
    if (formData.id) {
      await editTool({
        name: formData.name,
        description: formData.description,
        image: await generatePresignedUrl(formData.image),
        toolId: formData.id,
        code: formData.code,
      }).then((response: any) => {
        if (response.status) {
          showSnackbar('Tool edited successfully', 'success');
          setAddToolModal(false);
          fetchTools(1, 10, '');
          setFormData({
            id: '',
            name: '',
            description: '',
            image: '',
            code: '',
          });
        }
      });
    } else {
      await addTool({
        name: formData.name,
        description: formData.description,
        image: await generatePresignedUrl(formData.image),
        code: formData.code,
      }).then((response) => {
        if (response.status == 201) {
          showSnackbar('Tool created successfully', 'success');
          setAddToolModal(false);
          fetchTools(1, 10, '');
          setFormData({
            id: '',
            name: '',
            description: '',
            image: '',
            code: '',
          });
        }
      });
    }
  }

  const generatePresignedUrl = async (file: any) => {
    if (file.name) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_API_BASE_URL}/api/v1/admin/generate-presigned-url`,
          {
            params: {
              fileName: file.name,
              fileType: file.type,
              type: 10,
            },
          },
        );

        const presignedUrl = response.data.presignedUrl;

        if (!presignedUrl) {
          throw new Error('Presigned URL not found');
        }

        await axios.put(presignedUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
        });

        const cleanUrl = presignedUrl.split('?')[0];

        return cleanUrl;
      } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
      }
    } else {
      return file;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}
      <AddToolModal
        open={addToolModal}
        onClose={() => {
          setAddToolModal(false);
          setFormData({
            id: '',
            name: '',
            description: '',
            image: '',
            code: '',
          });
          setPreview('');
        }}
        onSaved={() => {
          handleSubmit();
        }}
        setFormData={setFormData}
        formData={formData}
        preview={preview}
        setPreview={setPreview}
      />
      <SmartFilter
        title="Tools"
        createFilter={['SEARCH', 'ADD']}
        filterHeader={'Filter Tool'}
        onNavigate={() => {
          setAddToolModal(true);
        }}
        onSaved={handleSaveRefresh}
        addDrawerSize="lg"
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        <Box
          ref={scrollRef}
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {toolList.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Material Found
            </Typography>
          ) : (
            <ToolList
              headerCells={headerToolTable}
              toolList={toolList}
              isLoading={isLoading}
              setAddToolModal={setAddToolModal}
              setFormData={setFormData}
              onUpdate={refeshList}
            />
          )}
        </Box>
        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalMaterial}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(ToolTable);
